import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { listOpenEvents, type EventFull } from "@/lib/eventRegistrationApi";

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const parseDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return { month: MONTHS_SHORT[(m ?? 1) - 1] ?? "", day: d ?? 0, year: y ?? 0 };
};

const EventAnnouncementBar = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  const { data: openEvents } = useQuery({
    queryKey: ["events", "open"],
    queryFn: listOpenEvents,
  });

  const event: EventFull | null =
    openEvents?.find(
      (e) =>
        e.show_card &&
        e.status === "open" &&
        (e.type === "mixed" || e.type === "open"),
    ) ?? null;

  const STORAGE_KEY = event ? `tweaq-event-bar-dismissed-${event.id}` : "";

  const isEventPage = event
    ? location.pathname.includes(`/events/${event.slug}`)
    : false;

  useEffect(() => {
    if (!event) return;
    if (isEventPage) return;
    if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    const t = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(t);
  }, [event, isEventPage, STORAGE_KEY]);

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  if (!event) return null;
  if (isEventPage || !visible) return null;

  const lang = location.pathname.split("/")[1] === "nl" ? "nl" : "en";
  const eventHref = `/${lang}/events/${event.slug}/apply`;
  const { month, day } = parseDate(event.date);

  return (
    <div
      className="fixed z-50 pointer-events-none inset-x-0 bottom-0 sm:inset-x-auto sm:left-auto sm:right-8 sm:bottom-9"
      role="region"
      aria-label="Event announcement"
      style={{ animation: "fade-in 0.5s ease-out" }}
    >
      <div className="relative pointer-events-auto bg-background border border-border sm:rounded-2xl shadow-[0_18px_50px_-10px_rgba(0,0,0,0.25)] sm:max-w-[560px] sm:w-[560px] transition-transform duration-200 hover:-translate-y-0.5">
        <button
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="absolute top-2.5 right-2.5 p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-md"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-4 px-5 py-5 pr-10">
          <div className="shrink-0 flex flex-col items-center justify-center w-14 h-16 rounded-lg bg-accent text-accent-foreground">
            <span className="text-[10px] font-display font-semibold tracking-[0.15em] uppercase leading-none">
              {month}
            </span>
            <span className="mt-1.5 text-xl font-display font-bold leading-none">
              {day}
            </span>
          </div>

          <div className="flex-1 min-w-0 flex flex-col">
            {event.label && (
              <p className="text-sm font-semibold text-foreground leading-tight">
                {event.label}
              </p>
            )}
            {event.card_summary && (
              <p className="mt-1 text-sm text-muted-foreground leading-snug">
                {event.card_summary}
              </p>
            )}
          </div>

          <Link
            to={eventHref}
            className="group shrink-0 self-center inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent/80 transition-colors whitespace-nowrap"
          >
            <span>View</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EventAnnouncementBar;
